import { Link } from 'react-router-dom';
import { movies, categories } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import SEO from '@/components/feature/SEO';

export default function Home() {
  const hotMovies = movies.filter((m) => m.isHot).slice(0, 6);
  const recommendedMovies = movies.filter((m) => m.isRecommended).slice(0, 8);
  const newMovies = movies.filter((m) => m.isNew).slice(0, 8);
  const topRated = [...movies].sort((a, b) => b.rating - a.rating).slice(0, 10);

  const heroMovie = movies[0];

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '8090影院',
    url: window.location.origin,
    description: '8090影院网聚合免费电影大全资源，高清电影在线观看国内外大片，打造不缓冲影院体验。',
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '8090影院',
    url: window.location.origin,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${window.location.origin}/category/all?search={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEO
        title="8090影院 - 免费电影大全高清电影在线观看 | 国内外大片不缓冲影院电影无广告"
        description="8090影院网聚合免费电影大全资源，高清电影在线观看国内外大片，打造不缓冲影院体验，电影无广告干扰，海量影片随心点播。8090影院，您的专属在线电影库。"
        keywords="8090影院,免费电影大全,高清电影在线,国内外大片,不缓冲影院,电影无广告,免费高清观看,在线电影库"
        structuredData={[organizationSchema, websiteSchema]}
      />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[680px] overflow-hidden">
          <img
            src={heroMovie.posterLarge}
            alt={heroMovie.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent" />

          <div className="relative z-10 h-full w-full px-4 md:px-6">
            <div className="max-w-[1400px] mx-auto h-full flex items-center">
              <div className="w-full md:w-[55%] pt-16">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1.5 bg-accent-500 text-foreground-50 text-xs font-semibold rounded-full">
                    正在热播
                  </span>
                  <span className="text-sm text-foreground-400">{heroMovie.year} · {heroMovie.region}</span>
                </div>
                <h1 className="text-3xl md:text-5xl font-bold text-foreground-100 font-heading mb-3 leading-tight">
                  {heroMovie.title}
                </h1>
                <p className="text-sm md:text-base font-heading text-foreground-500 mb-4">{heroMovie.titleEn}</p>

                <div className="flex items-center gap-4 mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-star-fill text-primary-500"></i>
                    </div>
                    <span className="text-lg font-bold text-primary-500">{heroMovie.rating}</span>
                    <span className="text-sm text-foreground-500">/ 10</span>
                  </div>
                  <span className="text-sm text-foreground-500">{heroMovie.duration}</span>
                  <span className="text-sm text-foreground-500">{heroMovie.category}</span>
                </div>

                <p className="text-sm text-foreground-400 leading-relaxed mb-6 max-w-[520px] line-clamp-3">
                  {heroMovie.description}
                </p>

                <div className="flex items-center gap-3 flex-wrap">
                  <Link
                    to={`/article/${heroMovie.id}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-950 font-semibold rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-play-fill"></i>
                    </div>
                    立即观看
                  </Link>
                  <Link
                    to={`/category/${categories.find((c) => c.name === heroMovie.category)?.slug || 'all'}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-background-100/80 backdrop-blur-sm text-foreground-200 font-medium rounded-full text-sm hover:bg-background-200 transition-colors whitespace-nowrap border border-background-300/30"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-apps-2-line"></i>
                    </div>
                    更多{heroMovie.category}
                  </Link>
                </div>

                <div className="flex items-center gap-2 mt-6 text-xs text-foreground-500">
                  <span>导演：{heroMovie.director}</span>
                  <span className="text-foreground-700">|</span>
                  <span>主演：{heroMovie.actors.slice(0, 3).join(' / ')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background-50 to-transparent" />
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-1 h-5 bg-primary-500 rounded-full" />
              <h2 className="text-lg font-bold text-foreground-100 font-heading">电影分类</h2>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-100 hover:bg-background-200 transition-colors group"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500/10 group-hover:bg-primary-500/20 transition-colors">
                    <i className={`${cat.icon} text-primary-500 text-lg`}></i>
                  </div>
                  <span className="text-xs font-medium text-foreground-300 group-hover:text-foreground-100 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Movies */}
        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-1 h-5 bg-accent-500 rounded-full" />
                <h2 className="text-lg font-bold text-foreground-100 font-heading">热播推荐</h2>
              </div>
              <Link
                to="/category/all"
                className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors"
              >
                查看全部
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {hotMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Recommended Movies */}
        <section className="w-full px-4 md:px-6 py-8 bg-background-100/50">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-1 h-5 bg-primary-500 rounded-full" />
                <h2 className="text-lg font-bold text-foreground-100 font-heading">编辑精选</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {recommendedMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* New Releases */}
        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-1 h-5 bg-secondary-500 rounded-full" />
                <h2 className="text-lg font-bold text-foreground-100 font-heading">最新上架</h2>
              </div>
              <Link
                to="/category/all?sort=new"
                className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors"
              >
                查看全部
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {newMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Top Rated Rankings */}
        <section className="w-full px-4 md:px-6 py-8 bg-background-100/50">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-1 h-5 bg-primary-500 rounded-full" />
              <h2 className="text-lg font-bold text-foreground-100 font-heading">高分排行榜</h2>
            </div>
            <div className="bg-background-100 rounded-xl border border-background-200/50 overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2">
                {topRated.map((movie, index) => (
                  <Link
                    key={movie.id}
                    to={`/article/${movie.id}`}
                    className="flex items-center gap-4 p-4 hover:bg-background-200/50 transition-colors group"
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-bold shrink-0 ${
                      index < 3
                        ? 'bg-primary-500 text-background-950'
                        : 'bg-background-200 text-foreground-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="w-12 h-16 rounded-lg overflow-hidden shrink-0 bg-background-200">
                      <img src={movie.poster} alt={movie.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground-200 group-hover:text-primary-500 transition-colors truncate">
                        {movie.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{movie.year} · {movie.category}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-primary-500 text-xs"></i>
                      </div>
                      <span className="text-sm font-bold text-primary-500">{movie.rating}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative w-full px-4 md:px-6 py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20background%20with%20movie%20theater%20seats%20and%20warm%20ambient%20lighting%2C%20abstract%20atmospheric%20style%2C%20soft%20glow%20from%20screen%2C%20elegant%20dark%20tone&width=1600&height=500&seq=cta-bg-cinema&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-background-50/80" />
          <div className="relative z-10 max-w-[800px] mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading mb-4">
              随时随地，免费看大片
            </h2>
            <p className="text-foreground-400 mb-8 max-w-[500px] mx-auto leading-relaxed">
              8090影院聚合海量高清电影资源，无广告、不缓冲，为您打造最纯净的在线观影体验。即刻开始探索您的专属电影世界。
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-950 font-semibold rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              开始探索
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}